/**
 * M3X.WORKSPACE F.6 — Tab strip at content top.
 *
 * Per spec §0.3 — intentionally redundant with Rail 2. Survives when
 * Rail 2 is Thin or Hidden so the active sub-domain's screens are
 * always reachable from the content area itself.
 *
 * Horizontal scroll for long screen sets. Active tab carries the
 * brand-gradient accent + aria-current="page".
 *
 * M3X.WORKSPACE-CLOSURE Session 2 PR 4 — chip per tab when its
 * screen has a non-zero attention count.
 */

import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";

import { type Screen, type SubDomain, screenForRoute } from "../navigation/registry";


export interface ContentTabStripProps {
  subDomain: SubDomain | null;
  locale?: "en" | "ar";
  /** PR 4 — per-screen counts. Tab gets a chip when count > 0. */
  attentionByScreen?: Record<string, number>;
}


function _CountChip({
  count,
  attention,
}: {
  count: number;
  attention?: boolean;
}) {
  if (count <= 0) return null;
  const isAttention = attention || count >= 10;
  return (
    <span
      data-count-chip
      className="ms-1.5 inline-flex items-center rounded-full px-1.5 py-px text-[10px] font-semibold leading-none"
      style={{
        backgroundColor: isAttention ? "#fef2f2" : "#eef2ff",
        color: isAttention ? "#dc2626" : "#4338ca",
      }}
    >
      {count}
    </span>
  );
}


export function ContentTabStrip({
  subDomain,
  locale = "en",
  attentionByScreen = {},
}: ContentTabStripProps) {
  const location = useLocation();
  if (!subDomain || subDomain.screens.length < 2) return null;

  // M3X.WORKSPACE-CLOSURE Session 2.9 PR 6 — single source of truth for
  // active state. screenForRoute uses longest-prefix-wins so when Grid
  // (/operations/attendance) and Bulk Adjustments
  // (/operations/attendance/bulk-adjustments) coexist, only ONE wins.
  const activeScreenId =
    screenForRoute(location.pathname)?.screen.id ?? null;

  return (
    <nav
      aria-label="Screens"
      className="flex flex-shrink-0 items-center gap-1 overflow-x-auto border-b border-border-default bg-surface-default px-3"
    >
      {subDomain.screens.map((screen: Screen) => {
        const isActive = activeScreenId === screen.id;
        const count = attentionByScreen[screen.id] ?? 0;
        return (
          <Link
            key={screen.id}
            to={screen.route}
            data-screen-tab={screen.id}
            aria-current={isActive ? "page" : undefined}
            className={[
              "relative shrink-0 px-3 py-2 text-sm",
              isActive
                ? "font-medium text-text-primary after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-t after:bg-brand-gradient-h"
                : "text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            {locale === "ar" ? screen.labelAr : screen.labelEn}
            <_CountChip count={count} attention={screen.attention} />
          </Link>
        );
      })}
    </nav>
  );
}


export default ContentTabStrip;
